import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Label,
  Spinner,
  Table,
  TextInput,
  Alert,
} from 'flowbite-react';
import Title from '../../components/Title';
import Button from '../../components/Button';
import PaginationFlowBite from '../../components/PaginationFlowBite';
import {
  accountManagersAPI,
  AccountManager,
  ACCOUNT_MANAGER_DUPLICATE_EMAIL,
  ACCOUNT_MANAGER_FORBIDDEN,
  ACCOUNT_MANAGER_NOT_FOUND,
  ACCOUNT_MANAGER_OPERATOR_UNVERIFIED,
  ACCOUNT_MANAGER_UNAVAILABLE,
  getAccountManagerRefusal,
  isAccountManagersSyncDisabledError,
  isAccountManagersSyncEmptyRefused,
  SyncAccountManagersResponse,
} from '../../api/account-managers';
import { usePermissions } from '../../hooks/usePermissions';
import { useOperatorIdentity } from '../../hooks/useOperatorIdentity';
import { useOperatorToken } from '../../hooks/useOperatorToken';
import {
  accountManagersStrings as S,
  formatMessage,
} from './AccountManagers.strings';

/**
 * Whether the HubSpot sync is offered, driven at runtime by `syncAvailable`
 * on the list response (HubSpot decommission block 3, task 53):
 * - `unknown`: the first list response has not arrived yet (no button, so a
 *   frozen roster never flashes it);
 * - `available`: `syncAvailable:true`, or the field is absent (an older DS),
 *   or the list could not be loaded: today's button;
 * - `frozen`: `syncAvailable:false`, or the sync answered 409
 *   `ACCOUNT_MANAGERS_SYNC_DISABLED`: no button, the frozen note instead.
 */
type SyncState = 'unknown' | 'available' | 'frozen';

/** A typed sync refusal shown in its own banner instead of the generic error. */
type SyncNotice = 'disabled' | 'emptyRefused';

/** A failed roster write: its text, and whether signing in again may fix it. */
interface WriteError {
  message: string;
  unverified: boolean;
}

/** DS requires a first AND a last name (it stores both). */
const FIRST_AND_LAST_NAME = /^\S+(\s+\S+)+$/;
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_NEW_MANAGER = { fullName: '', email: '' };

/** The operator-facing text of a failed add / activate / deactivate. */
const writeErrorOf = (error: unknown): WriteError => {
  const refusal = getAccountManagerRefusal(error);
  switch (refusal?.code) {
    case ACCOUNT_MANAGER_DUPLICATE_EMAIL:
      return { message: S.add.duplicateEmail, unverified: false };
    case ACCOUNT_MANAGER_OPERATOR_UNVERIFIED:
      return { message: S.refusal.unverified, unverified: true };
    case ACCOUNT_MANAGER_FORBIDDEN:
      return { message: S.refusal.forbidden, unverified: false };
    case ACCOUNT_MANAGER_UNAVAILABLE:
      return { message: S.refusal.unavailable, unverified: false };
    case ACCOUNT_MANAGER_NOT_FOUND:
      return { message: S.refusal.notFound, unverified: false };
    default:
      return { message: S.refusal.generic, unverified: false };
  }
};

const displayName = (am: AccountManager): string => {
  const name = `${am.firstName} ${am.lastName}`.trim();
  return am.isActive ? name : formatMessage(S.table.inactiveName, { name });
};

const AccountManagers: React.FC = () => {
  const [accountManagers, setAccountManagers] = useState<AccountManager[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [syncResult, setSyncResult] =
    useState<SyncAccountManagersResponse | null>(null);
  const [syncState, setSyncState] = useState<SyncState>('unknown');
  const [syncNotice, setSyncNotice] = useState<SyncNotice | null>(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [showInactive, setShowInactive] = useState(false);
  const [writeError, setWriteError] = useState<WriteError | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newManager, setNewManager] = useState(EMPTY_NEW_MANAGER);
  const [addError, setAddError] = useState<WriteError | null>(null);
  const [adding, setAdding] = useState(false);

  const { canAssignAccountManagers } = usePermissions();
  const canManage = canAssignAccountManagers();
  const readOperator = useOperatorIdentity();
  const { getOperatorToken, signInAgain } = useOperatorToken();

  const fetchAccountManagers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await accountManagersAPI.getAccountManagers({
        page: page,
        perPage: perPage,
        activeOnly: !showInactive,
      });
      setAccountManagers(response.data || []);
      setTotalPages(response.pages || 1);
      setTotal(response.total || 0);
      setSyncState(response.syncAvailable === false ? 'frozen' : 'available');
    } catch (error) {
      console.error('Error fetching account managers:', error);
      setError(error instanceof Error ? error.message : S.fetchError);
      setAccountManagers([]);
      setTotal(0);
      // No answer from DS: keep today's button rather than hiding it on a
      // transient failure. A sync under the switch still answers 409.
      setSyncState((current) =>
        current === 'unknown' ? 'available' : current,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAccountManagers();
  }, [page, perPage, showInactive]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePerPage = (value: { value: number }) => {
    setPerPage(value.value);
    setPage(1);
  };

  const handleSync = async () => {
    if (syncState !== 'available') {
      return;
    }
    setSyncing(true);
    setError(null);
    setSyncResult(null);
    setSyncNotice(null);
    try {
      const result = await accountManagersAPI.syncFromHubSpot();
      if (isAccountManagersSyncEmptyRefused(result)) {
        // DS wrote nothing: the roster did not change, so no refetch.
        setSyncNotice('emptyRefused');
        return;
      }
      setSyncResult(result);
      if (result.success) {
        await fetchAccountManagers();
      }
    } catch (error) {
      if (isAccountManagersSyncDisabledError(error)) {
        // The switch went ON after the page loaded: the roster is frozen.
        setSyncState('frozen');
        setSyncNotice('disabled');
        return;
      }
      console.error('Error syncing account managers:', error);
      setError(error instanceof Error ? error.message : S.sync.error);
    } finally {
      setSyncing(false);
    }
  };

  const handleShowInactive = (checked: boolean) => {
    setShowInactive(checked);
    setPage(1);
  };

  /** The operator for ONE write: e-mail plus a freshly renewed id token. */
  const operatorForWrite = async () => {
    const token = await getOperatorToken();
    return { ...readOperator(), token };
  };

  const handleSetActive = async (am: AccountManager, isActive: boolean) => {
    if (pendingId) {
      return;
    }
    setPendingId(am.id);
    setWriteError(null);
    try {
      await accountManagersAPI.setAccountManagerActive(
        am.id,
        isActive,
        await operatorForWrite(),
      );
      await fetchAccountManagers();
    } catch (error) {
      setWriteError(writeErrorOf(error));
      if (getAccountManagerRefusal(error)?.code === ACCOUNT_MANAGER_NOT_FOUND) {
        await fetchAccountManagers();
      }
    } finally {
      setPendingId(null);
    }
  };

  const openAdd = () => {
    setNewManager(EMPTY_NEW_MANAGER);
    setAddError(null);
    setAddOpen(true);
  };

  const cancelAdd = () => {
    if (adding) {
      return;
    }
    setAddOpen(false);
    setAddError(null);
  };

  const handleAdd = async () => {
    if (adding) {
      return;
    }
    const fullName = newManager.fullName.trim().replace(/\s+/g, ' ');
    const email = newManager.email.trim();
    if (!FIRST_AND_LAST_NAME.test(fullName)) {
      setAddError({ message: S.add.nameInvalid, unverified: false });
      return;
    }
    if (!EMAIL_SHAPE.test(email)) {
      setAddError({ message: S.add.emailInvalid, unverified: false });
      return;
    }
    setAdding(true);
    setAddError(null);
    try {
      await accountManagersAPI.createAccountManager(
        { fullName, email },
        await operatorForWrite(),
      );
      setAddOpen(false);
      setNewManager(EMPTY_NEW_MANAGER);
      await fetchAccountManagers();
    } catch (error) {
      setAddError(writeErrorOf(error));
    } finally {
      setAdding(false);
    }
  };

  const signInAgainLink = (
    <button
      type="button"
      onClick={signInAgain}
      className="ml-2 text-xs font-semibold text-primary-700 hover:underline"
    >
      {S.refusal.signInAgain}
    </button>
  );

  const columnCount = canManage ? 4 : 3;

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Title>{S.title}</Title>
          {syncState === 'available' && (
            <Button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2"
            >
              {syncing && <Spinner size="sm" />}
              {syncing ? S.sync.syncing : S.sync.button}
            </Button>
          )}
        </div>

        {syncState === 'frozen' && (
          <Alert
            color="info"
            className="mb-4"
            data-testid="account-managers-roster-frozen"
          >
            {S.frozen}
          </Alert>
        )}

        {syncNotice === 'disabled' && (
          <Alert
            color="warning"
            className="mb-4"
            onDismiss={() => setSyncNotice(null)}
            data-testid="account-managers-sync-disabled"
          >
            {S.sync.disabled}
          </Alert>
        )}

        {syncNotice === 'emptyRefused' && (
          <Alert
            color="warning"
            className="mb-4"
            onDismiss={() => setSyncNotice(null)}
            data-testid="account-managers-sync-empty-refused"
          >
            {S.sync.emptyRefused}
          </Alert>
        )}

        {writeError && (
          <Alert
            color="failure"
            className="mb-4"
            onDismiss={() => setWriteError(null)}
            data-testid="account-managers-write-error"
          >
            {writeError.message}
            {writeError.unverified && signInAgainLink}
          </Alert>
        )}

        {error && (
          <Alert color="failure" className="mb-4">
            <span className="font-medium">{S.errorLabel}</span> {error}
          </Alert>
        )}

        {syncResult && (
          <Alert
            color={syncResult.success ? 'success' : 'failure'}
            className="mb-4"
            onDismiss={() => setSyncResult(null)}
          >
            <div>
              <p className="font-medium mb-2">
                {syncResult.success ? S.sync.success : S.sync.failed}
              </p>
              {syncResult.success && (
                <ul className="list-disc list-inside text-sm">
                  <li>
                    {formatMessage(S.sync.totalSynced, {
                      count: syncResult.synced,
                    })}
                  </li>
                  <li>
                    {formatMessage(S.sync.created, {
                      count: syncResult.created,
                    })}
                  </li>
                  <li>
                    {formatMessage(S.sync.updated, {
                      count: syncResult.updated,
                    })}
                  </li>
                  <li>
                    {formatMessage(S.sync.deactivated, {
                      count: syncResult.deactivated,
                    })}
                  </li>
                </ul>
              )}
              {syncResult.message && (
                <p className="text-sm mt-2">{syncResult.message}</p>
              )}
            </div>
          </Alert>
        )}

        <div className="flex justify-between items-center mb-4">
          <div className="text-sm text-gray-600">
            {formatMessage(S.showing, {
              shown: accountManagers.length,
              total,
            })}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="account-managers-show-inactive"
              checked={showInactive}
              onChange={(event) => handleShowInactive(event.target.checked)}
            />
            <Label htmlFor="account-managers-show-inactive">
              {S.showInactive}
            </Label>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Spinner size="xl" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>{S.table.name}</Table.HeadCell>
                <Table.HeadCell>{S.table.email}</Table.HeadCell>
                <Table.HeadCell>{S.table.members}</Table.HeadCell>
                {canManage && (
                  <Table.HeadCell>
                    <span className="sr-only">{S.table.actions}</span>
                  </Table.HeadCell>
                )}
              </Table.Head>
              <Table.Body className="divide-y">
                {accountManagers.length === 0 ? (
                  <Table.Row>
                    <Table.Cell
                      colSpan={columnCount}
                      className="text-center py-8"
                    >
                      {syncState === 'available'
                        ? S.table.emptyWithSync
                        : S.table.empty}
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  accountManagers.map((am) => (
                    <Table.Row
                      key={am.id}
                      className="bg-white hover:bg-gray-50"
                      data-testid={`account-manager-row-${am.id}`}
                    >
                      <Table.Cell
                        className={
                          am.isActive ? 'font-medium' : 'font-medium text-gray-500'
                        }
                      >
                        {displayName(am)}
                      </Table.Cell>
                      <Table.Cell>{am.email}</Table.Cell>
                      <Table.Cell>{am.memberCount?.toLocaleString('en-US')}</Table.Cell>
                      {canManage && (
                        <Table.Cell className="text-right">
                          <button
                            type="button"
                            disabled={pendingId !== null}
                            onClick={() => handleSetActive(am, !am.isActive)}
                            className="text-xs font-semibold text-primary-700 hover:underline disabled:opacity-50 disabled:no-underline"
                          >
                            {am.isActive
                              ? S.actions.deactivate
                              : S.actions.activate}
                          </button>
                        </Table.Cell>
                      )}
                    </Table.Row>
                  ))
                )}
                {canManage && !addOpen && (
                  <Table.Row className="bg-white">
                    <Table.Cell colSpan={columnCount}>
                      <button
                        type="button"
                        onClick={openAdd}
                        className="text-xs font-semibold text-primary-700 hover:underline"
                      >
                        {S.add.link}
                      </button>
                    </Table.Cell>
                  </Table.Row>
                )}
                {canManage && addOpen && (
                  <Table.Row
                    className="bg-white"
                    data-testid="account-managers-add-row"
                  >
                    <Table.Cell>
                      <TextInput
                        sizing="sm"
                        aria-label={S.add.fullName}
                        placeholder={S.add.fullName}
                        value={newManager.fullName}
                        disabled={adding}
                        onChange={(event) =>
                          setNewManager((current) => ({
                            ...current,
                            fullName: event.target.value,
                          }))
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <TextInput
                        sizing="sm"
                        type="email"
                        aria-label={S.add.email}
                        placeholder={S.add.email}
                        value={newManager.email}
                        disabled={adding}
                        onChange={(event) =>
                          setNewManager((current) => ({
                            ...current,
                            email: event.target.value,
                          }))
                        }
                      />
                      {addError && (
                        <p
                          role="alert"
                          className="mt-1 text-xs text-red-700"
                          data-testid="account-managers-add-error"
                        >
                          {addError.message}
                          {addError.unverified && signInAgainLink}
                        </p>
                      )}
                    </Table.Cell>
                    <Table.Cell />
                    <Table.Cell className="text-right">
                      <div className="flex justify-end items-center gap-2">
                        <Button
                          size="xs"
                          onClick={handleAdd}
                          disabled={adding}
                          isProcessing={adding}
                        >
                          {S.add.submit}
                        </Button>
                        <Button
                          size="xs"
                          variant="OUTLINED"
                          onClick={cancelAdd}
                          disabled={adding}
                        >
                          {S.add.cancel}
                        </Button>
                      </div>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="mt-2">
              <PaginationFlowBite
                tablePagination={{
                  pageSize: perPage,
                  total,
                  lastPage: totalPages,
                  currentPage: page,
                  onChange: handlePageChange,
                }}
                handlePerPage={handlePerPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AccountManagers;
